import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { ListProgramCandidates200 } from '@eureca/recruiter-api-sdk/types';
import type { CandidatePhaseStatus, CandidateSubscriptionStatus } from './program-management-model';

/**
 * EU-593 — listagem de candidatos do programa (`GET /programs/:id/candidates`
 * do BFF, proxy fino do Core). A RESPOSTA usa o tipo gerado do SDK
 * (`ListProgramCandidates200` — drift do contrato quebra o typecheck); o
 * input da query fica local porque o shape de arrays repetidos é mais
 * ergonômico que o union single-or-array do Kubb.
 */

export type CandidateDiversityToken =
  | 'mulheres'
  | 'transexuais'
  | 'amarelas'
  | 'indigenas'
  | 'pardas'
  | 'pretas'
  | 'pessoas-com-deficiencia';

export interface ProgramCandidatesFilters {
  subscriptionStatus?: CandidateSubscriptionStatus;
  phaseStatus?: CandidatePhaseStatus;
  qualified?: boolean;
  apt?: boolean;
  stateAcronyms?: string[];
  cityIds?: number[];
  courseTypeIds?: number[];
  courseAreaIds?: number[];
  courseGroupIds?: number[];
  diversity?: CandidateDiversityToken[];
  formationDateMin?: string;
  formationDateMax?: string;
}

export interface ProgramCandidatesQueryInput extends ProgramCandidatesFilters {
  programId: string;
  page: number;
  pageSize: number;
  stepTypeKey?: string;
  search?: string;
}

export type ProgramCandidatesResponse = ListProgramCandidates200;
export type ProgramCandidateRow = ListProgramCandidates200['items'][number];
export type CandidatePreview = ProgramCandidateRow['preview'];
export type CandidateFormation = NonNullable<CandidatePreview['formation']>;

export class ProgramCandidatesFetchError extends Error {
  readonly name = 'ProgramCandidatesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

function buildQueryString(input: ProgramCandidatesQueryInput): string {
  const params = new URLSearchParams();
  params.set('page', String(input.page));
  params.set('pageSize', String(input.pageSize));
  if (input.stepTypeKey) params.set('stepTypeKey', input.stepTypeKey);
  if (input.search) params.set('search', input.search);
  if (input.subscriptionStatus) params.set('subscriptionStatus', input.subscriptionStatus);
  if (input.phaseStatus) params.set('phaseStatus', input.phaseStatus);
  if (input.qualified !== undefined) params.set('qualified', String(input.qualified));
  if (input.apt !== undefined) params.set('apt', String(input.apt));
  for (const uf of input.stateAcronyms ?? []) params.append('stateAcronym', uf);
  for (const id of input.cityIds ?? []) params.append('cityId', String(id));
  for (const id of input.courseTypeIds ?? []) params.append('courseTypeId', String(id));
  for (const id of input.courseAreaIds ?? []) params.append('courseAreaId', String(id));
  for (const id of input.courseGroupIds ?? []) params.append('courseGroupId', String(id));
  for (const token of input.diversity ?? []) params.append('diversity', token);
  if (input.formationDateMin) params.set('formationDateMin', input.formationDateMin);
  if (input.formationDateMax) params.set('formationDateMax', input.formationDateMax);
  return params.toString();
}

async function fetchProgramCandidates(
  input: ProgramCandidatesQueryInput,
  signal?: AbortSignal
): Promise<ProgramCandidatesResponse> {
  const res = await fetchClient<ProgramCandidatesResponse>({
    method: 'GET',
    url: `/programs/${input.programId}/candidates?${buildQueryString(input)}`,
    signal,
  });
  if (res.error) {
    throw new ProgramCandidatesFetchError(
      `Failed to load program candidates (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

export function programCandidatesQueryKey(input: ProgramCandidatesQueryInput) {
  return ['recruiter', 'programs', 'candidates', input] as const;
}

export function programCandidatesQueryOptions(input: ProgramCandidatesQueryInput) {
  return queryOptions({
    queryKey: programCandidatesQueryKey(input),
    queryFn: ({ signal }) => fetchProgramCandidates(input, signal),
    // Troca de aba/página mantém a tabela anterior visível (sem flicker).
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof ProgramCandidatesFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
